import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { PerspectiveCamera } from "@react-three/drei";
import {
  XR,
  XROrigin,
  useXRInputSourceState,
  useXRControllerButtonEvent,
} from "@react-three/xr";
import type { XRControllerState } from "@react-three/xr";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import gsap from "gsap";

import { xrStore } from "./xrStore";
import { Dojo } from "../scene/Dojo";
import { HitFlashPool } from "../scene/HitFlashPool";
import { KataDemo } from "../scene/KataDemo";
import { HUD } from "../ui/HUD";
import { Menu } from "../ui/Menu";
import { ResultScreen3D, ResultOverlay } from "../ui/ResultScreen";
import { StoryBeat, StoryOverlay } from "../ui/StoryBeat";
import {
  TutorialDemo3D,
  TeachLessonPanel3D,
  TutorialBriefingScreen,
  TutorialOverlay,
} from "../ui/TeachPanel";
import { SkillTreeScreen } from "../ui/SkillTreeScreen";
import { SurvivalIntro3D, SurvivalIntroOverlay } from "../ui/SurvivalIntro";
import { GameManager } from "../game/GameManager";
import { SurvivalManager } from "../game/SurvivalManager";
import { useGameStore } from "../game/useGameStore";
import { SEATED_HEIGHT_OFFSET } from "../game/constants";
import { CAMPAIGN_PHASES } from "../game/campaign";
import { unlockRegisteredTracks, useTrack } from "../audio/useTrack";
import {
  isNativeQuestWrapper,
  isPwaLaunchContext,
} from "../telemetry/platform";
import { track, flush } from "../telemetry/telemetry";
import { primaryButton, secondaryButton, typography } from "../ui/theme";
import { INK } from "../ui/theme3d";
import {
  ShojiBacking,
  PanelText,
  PanelRule,
  Enso,
  GlyphChip,
  PanelErrorBoundary,
} from "../ui/panel3d";

// ─── GSAP ↔ R3F sync ──────────────────────────────────────────────────────────
// GSAP's default ticker is driven by window.requestAnimationFrame, which STOPS
// firing once an immersive XR session takes over the frame loop. Without driving
// GSAP from R3F's loop, the hit/miss timelines in <Block> never advance, their
// onComplete never fires, and blocks freeze on screen blocking the player's view.
// We remove GSAP's own ticker callback and advance it from useFrame instead
// (useFrame runs off the XR session's frame loop while in VR).
gsap.ticker.lagSmoothing(0);
gsap.ticker.remove(gsap.updateRoot);

function GsapTicker() {
  useFrame(({ clock }) => {
    gsap.updateRoot(clock.elapsedTime);
  });
  return null;
}

// ─── Pause toggle ─────────────────────────────────────────────────────────────
function togglePause() {
  const { phase, setPhase } = useGameStore.getState();
  if (phase === "playing") setPhase("paused");
  else if (phase === "paused") setPhase("playing");
}

// Restart the current run from scratch — reuses the same store actions the
// win/fail ResultOverlay already uses for its "retry" button, so behaviour
// (score/combo reset, fresh countdown) stays identical whether you restart
// after finishing a run or mid-run from pause.
function restartRun() {
  const s = useGameStore.getState();
  if (s.phase !== "paused") return;
  if (s.mode === "survival") s.retrySurvival();
  else s.retryCampaignPhase();
}

// Exit to the main menu — same resetGame() the ResultOverlay "Menu" button
// uses. Also ends the live WebXR session: resetGame() only flips React state
// to phase "menu", and the DOM <Menu> overlay it reveals doesn't render
// inside an active immersive session, so without this a VR player pressing
// "exit" would be stuck looking at the dōjō with no menu visible.
function exitToMenu() {
  const s = useGameStore.getState();
  if (s.phase !== "paused") return;
  // In the installed PWA there is no 2D page behind the session — the world-
  // space MenuScreen3D is the menu, so the session must stay alive. On
  // desktop/browser the DOM overlay menu is the target surface: end the
  // session so it becomes reachable (DOM UI never renders inside immersive).
  if (!isPwaLaunchContext()) xrStore.getState().session?.end();
  s.resetGame();
}

type ControllerHand = "left" | "right";

function isActiveControllerHand(hand: ControllerHand) {
  const { settings } = useGameStore.getState();
  return settings.wield === "dual" || settings.singleHand === hand;
}

// Controller-driven pause controls. Must live inside <XR> to read input
// source state. Rendered with no visuals.
// - B (right) / Y (left): toggle pause/resume.
// - A (right) / X (left): restart the run — reuses skip's buttons since the
//   two phases (paused vs story) never overlap, so there's no real conflict.
// - Left thumbstick click: exit to menu. Deliberately not a face button —
//   this ends the run, so it sits on a button nobody brushes by accident.
// The binding below follows the active hand in single-hand mode.
function PauseControls() {
  // May be undefined until a controller connects; the hook tolerates undefined
  // (it reads via optional chaining), so the cast is safe at runtime.
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;

  useXRControllerButtonEvent(right, "b-button", (s) => {
    if (s === "pressed") togglePause();
  });
  useXRControllerButtonEvent(left, "y-button", (s) => {
    if (s === "pressed") togglePause();
  });
  useXRControllerButtonEvent(right, "a-button", (s) => {
    if (s === "pressed") restartRun();
  });
  useXRControllerButtonEvent(left, "x-button", (s) => {
    if (s === "pressed") restartRun();
  });
  useXRControllerButtonEvent(left, "xr-standard-thumbstick", (s) => {
    if (s === "pressed" && isActiveControllerHand("left")) exitToMenu();
  });
  useXRControllerButtonEvent(right, "xr-standard-thumbstick", (s) => {
    if (s === "pressed" && isActiveControllerHand("right")) exitToMenu();
  });

  return null;
}

// Win/fail result-screen actions — mirrors ResultOverlay's (ui/ResultScreen.tsx)
// two DOM buttons exactly (same store actions, same per-mode/pass/advance
// branching), so behaviour is identical whether triggered by mouse or
// controller. Needed because ResultOverlay is a fixed-position DOM overlay
// that, like the rest of this app's DOM UI, never renders inside an active
// immersive session — without a controller-reachable equivalent, a VR player
// finishing a run has no way to leave the result screen.
function resultRetryAction() {
  const s = useGameStore.getState();
  // Guard before unlocking: PauseControls/ResultControls/TutorialControls
  // all stay mounted and listen on the same B/Y/A/X buttons regardless of
  // phase, so a B-press to pause also reaches this handler. Calling
  // unlockRegisteredTracks() unconditionally used to force-seek every
  // registered <audio> that's currently paused (including a mid-song
  // campaign track paused via the in-game pause menu) back to 0 — the
  // "resume restarts the song and wipes on-screen targets" bug.
  if (s.phase !== "win" && s.phase !== "fail") return;
  unlockRegisteredTracks();
  if (s.resultDetailsOpen) {
    s.setResultDetailsOpen(false);
    return;
  }
  if (s.mode === "survival") s.retrySurvival();
  else s.retryCampaignPhase();
}

function resultPrimaryAction() {
  const s = useGameStore.getState();
  if (s.phase !== "win" && s.phase !== "fail") return;
  unlockRegisteredTracks();
  if (s.resultDetailsOpen) {
    // Performance details are a self-contained result view. Both face-button
    // families return to the summary so no result action can accidentally
    // end the XR session while this panel is open.
    s.setResultDetailsOpen(false);
    return;
  }
  if (s.mode === "survival") {
    s.retrySurvival();
    return;
  }
  const passed = s.lastRunPassed === true;
  if (!passed) {
    s.resetGame();
    return;
  }
  const canAdvance = s.lastRunPhaseIndex < CAMPAIGN_PHASES.length - 1;
  const hasStory = CAMPAIGN_PHASES[s.lastRunPhaseIndex]?.story != null;
  if (hasStory) s.goToStory();
  else if (canAdvance) s.advanceCampaignPhase();
  else s.resetGame();
}

function resultExitAction() {
  const s = useGameStore.getState();
  if (s.phase !== "win" && s.phase !== "fail") return;
  // PWA: stay in the session — the world-space menu is the destination.
  if (!isPwaLaunchContext()) xrStore.getState().session?.end();
  s.resetGame();
}

function resultDetailsAction() {
  const s = useGameStore.getState();
  if (s.phase !== "win" && s.phase !== "fail") return;
  s.toggleResultDetails();
}

// Controller-driven result-screen controls. Same button family as
// PauseControls — safe to reuse since "paused" and "win"/"fail" never
// overlap — so the player only has to learn one scheme:
// - B (right) / Y (left): primary/forward action (continue, next phase, or
//   menu — whatever ResultOverlay's primary button would do).
// - A (right) / X (left): retry.
// - Left thumbstick click: exit to menu, regardless of what "primary" does
//   — the same guaranteed escape hatch PauseControls offers.
function ResultControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;

  useXRControllerButtonEvent(right, "b-button", (s) => {
    if (s === "pressed") resultPrimaryAction();
  });
  useXRControllerButtonEvent(left, "y-button", (s) => {
    if (s === "pressed") resultPrimaryAction();
  });
  useXRControllerButtonEvent(right, "a-button", (s) => {
    if (s === "pressed") resultRetryAction();
  });
  useXRControllerButtonEvent(left, "x-button", (s) => {
    if (s === "pressed") resultRetryAction();
  });
  useXRControllerButtonEvent(left, "xr-standard-thumbstick", (s) => {
    if (s === "pressed") resultExitAction();
  });
  useXRControllerButtonEvent(right, "xr-standard-thumbstick", (s) => {
    if (s === "pressed") resultDetailsAction();
  });

  return null;
}

// Controller-driven story skip: A (right hand) / X (left hand) — distinct
// from pause's B/Y so the two never fight over the same button.
function StorySkipControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;

  const skip = () => {
    if (useGameStore.getState().phase === "story")
      useGameStore.getState().finishStory();
  };

  useXRControllerButtonEvent(right, "a-button", (s) => {
    if (s === "pressed") skip();
  });
  useXRControllerButtonEvent(left, "x-button", (s) => {
    if (s === "pressed") skip();
  });

  return null;
}

function tutorialPrimaryAction() {
  const s = useGameStore.getState();
  // See resultRetryAction above: guard before unlocking, since this handler
  // stays mounted and listening regardless of phase. Must cover "teach": in
  // the installed PWA the lesson is navigated entirely in VR (TeachPanel3D),
  // and with GHOST_DEMO_ENABLED off the flow is teach → countdown, never
  // "consolidate" — so gating on consolidate alone left the campaign lesson
  // with no way to advance in VR (the player was stuck on phase 1's lesson).
  if (s.phase !== "teach" && s.phase !== "consolidate") return;
  unlockRegisteredTracks();
  s.advanceTutorial();
}

function tutorialSkipAction() {
  const s = useGameStore.getState();
  if (s.phase !== "teach" && s.phase !== "consolidate") return;
  unlockRegisteredTracks();
  s.skipTutorial();
}

function tutorialBackAction() {
  const s = useGameStore.getState();
  // backTutorial() itself already guards on phase === "teach" and clamps at
  // index 0 (see useGameStore.ts) — this mirrors the DOM briefing's
  // hasPreviousLine-gated Back button, which is teach-only too.
  if (s.phase !== "teach") return;
  s.backTutorial();
}

function tutorialExitAction() {
  const s = useGameStore.getState();
  if (s.phase !== "teach" && s.phase !== "demo" && s.phase !== "consolidate")
    return;
  // PWA: stay in the session — the world-space menu is the destination.
  if (!isPwaLaunchContext()) xrStore.getState().session?.end();
  s.resetGame();
}

function TutorialControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;

  useXRControllerButtonEvent(right, "b-button", (s) => {
    if (s === "pressed") tutorialPrimaryAction();
  });
  useXRControllerButtonEvent(left, "y-button", (s) => {
    if (s === "pressed") tutorialPrimaryAction();
  });
  useXRControllerButtonEvent(right, "a-button", (s) => {
    if (s === "pressed") tutorialSkipAction();
  });
  useXRControllerButtonEvent(left, "x-button", (s) => {
    if (s === "pressed") tutorialSkipAction();
  });
  useXRControllerButtonEvent(left, "xr-standard-thumbstick", (s) => {
    if (s === "pressed") tutorialExitAction();
  });

  return null;
}

// Additive to TutorialControls' B/Y (next) and A/X (skip) — those stay
// exactly as they were, the validated fallback. This adds stick left/right
// as a symmetric back/forward gesture matching the approved DOM briefing's
// Back/Next buttons, mirroring MenuNavControls' armed-latch debounce so a
// held deflection steps once, not every frame.
function TutorialNavControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;
  const armed = useRef(true);

  useFrame(() => {
    const phase = useGameStore.getState().phase;
    if (phase !== "teach" && phase !== "consolidate") {
      armed.current = true;
      return;
    }

    const readStick = (c: XRControllerState | undefined) =>
      c?.gamepad?.["xr-standard-thumbstick"];
    const ls = readStick(left);
    const rs = readStick(right);
    const x =
      Math.abs(rs?.xAxis ?? 0) > Math.abs(ls?.xAxis ?? 0)
        ? (rs?.xAxis ?? 0)
        : (ls?.xAxis ?? 0);

    const DEFLECT = 0.7;
    const CENTER = 0.25;

    if (!armed.current) {
      if (Math.abs(x) < CENTER) armed.current = true;
      return;
    }

    if (Math.abs(x) >= DEFLECT) {
      if (x < 0) tutorialBackAction();
      else tutorialPrimaryAction();
      armed.current = false;
    }
  });

  return null;
}

// Full-screen post bloom is disabled while an immersive XR session is active:
// the current EffectComposer path black-screens on Quest. Keep it as a desktop
// A/B toggle only while the XR-safe sword glow carries the headset look.
function toggleBloomForDesktopOnly() {
  const s = useGameStore.getState();
  if (xrStore.getState().session) {
    if (s.bloomEnabled) s.setBloomEnabled(false);
    console.warn(
      "Bloom post-processing is disabled during immersive XR on Quest.",
    );
    return;
  }
  s.toggleBloom();
  console.info(`Bloom ${useGameStore.getState().bloomEnabled ? "on" : "off"}`);
}

function BloomToggleControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  useXRControllerButtonEvent(right, "xr-standard-thumbstick", (s) => {
    if (s !== "pressed") return;
    const phase = useGameStore.getState().phase;
    if (
      phase === "paused" ||
      phase === "win" ||
      phase === "fail" ||
      phase === "teach" ||
      phase === "demo" ||
      phase === "consolidate"
    ) {
      return;
    }
    toggleBloomForDesktopOnly();
  });
  return null;
}

function SwordGlowToggleControls() {
  const left = useXRInputSourceState("controller", "left") as XRControllerState;
  useXRControllerButtonEvent(left, "xr-standard-thumbstick", (s) => {
    if (s !== "pressed") return;
    const phase = useGameStore.getState().phase;
    if (
      phase === "paused" ||
      phase === "win" ||
      phase === "fail" ||
      phase === "teach" ||
      phase === "demo" ||
      phase === "consolidate"
    ) {
      return;
    }
    useGameStore.getState().toggleSwordGlow();
    console.info(
      `Sword glow ${useGameStore.getState().swordGlowEnabled ? "on" : "off"}`,
    );
  });
  return null;
}

// ─── Pause overlay (world space, visible in VR) ───────────────────────────────
function startMenuCampaign() {
  unlockRegisteredTracks();
  const s = useGameStore.getState();
  s.startCampaignPhase(s.selectedPhaseIndex);
}

function startMenuSurvival() {
  useGameStore.getState().openSurvivalIntro();
}

function startSurvivalRun() {
  const state = useGameStore.getState();
  if (state.survivalAudioStatus === "error") {
    state.retrySurvivalAudio();
    return;
  }
  if (state.survivalAudioStatus !== "ready") return;
  unlockRegisteredTracks();
  state.startSurvival();
}

function exitSurvivalIntro() {
  const state = useGameStore.getState();
  if (state.phase === "survival-intro") state.resetGame();
}

// B/Y (Campaign) and A/X (Survival) are direct shortcuts, kept exactly as
// they were — the cursor/trigger flow below is additive, not a
// replacement. If the ensō cursor ever misbehaves on-device, these two
// still get the player into the game unchanged.
function MenuControls({
  onExitApplication,
}: {
  onExitApplication: () => void;
}) {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;

  useXRControllerButtonEvent(right, "b-button", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    const state = useGameStore.getState();
    if (state.menuExitConfirm) state.setMenuExitConfirm(false);
    else startMenuCampaign();
  });
  useXRControllerButtonEvent(left, "y-button", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    const state = useGameStore.getState();
    if (state.menuExitConfirm) state.setMenuExitConfirm(false);
    else startMenuCampaign();
  });
  useXRControllerButtonEvent(right, "a-button", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    if (useGameStore.getState().menuExitConfirm) onExitApplication();
    else startMenuSurvival();
  });
  useXRControllerButtonEvent(left, "x-button", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    if (useGameStore.getState().menuExitConfirm) onExitApplication();
    else startMenuSurvival();
  });

  // Trigger confirms whichever item the ensō cursor currently rests on.
  useXRControllerButtonEvent(right, "xr-standard-trigger", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    const state = useGameStore.getState();
    if (state.menuExitConfirm) {
      if (state.menuExitCursor === "exit") onExitApplication();
      else state.setMenuExitConfirm(false);
    } else if (state.menuCursor === "campaign") startMenuCampaign();
    else if (state.menuCursor === "survival") startMenuSurvival();
    else state.setMenuExitConfirm(true);
  });
  useXRControllerButtonEvent(left, "xr-standard-trigger", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    const state = useGameStore.getState();
    if (state.menuExitConfirm) {
      if (state.menuExitCursor === "exit") onExitApplication();
      else state.setMenuExitConfirm(false);
    } else if (state.menuCursor === "campaign") startMenuCampaign();
    else if (state.menuCursor === "survival") startMenuSurvival();
    else state.setMenuExitConfirm(true);
  });

  // Grip flips the CUT row (MenuScreen3D) — the one setting with no
  // menuCursor slot of its own (see the comment above MenuNavControls).
  // Guarded against the exit-confirmation dialog like every other menu input.
  useXRControllerButtonEvent(right, "xr-standard-squeeze", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    if (useGameStore.getState().menuExitConfirm) return;
    toggleCutArrowInverted();
  });
  useXRControllerButtonEvent(left, "xr-standard-squeeze", (s) => {
    if (s !== "pressed" || useGameStore.getState().phase !== "menu") return;
    if (useGameStore.getState().menuExitConfirm) return;
    toggleCutArrowInverted();
  });

  return null;
}

function cycleWield() {
  const s = useGameStore.getState();
  s.setWield(s.settings.wield === "single" ? "dual" : "single");
}

function toggleTelemetryOptOut() {
  const s = useGameStore.getState();
  s.setTelemetryOptOut(!s.telemetryOptOut);
}

function toggleCutArrowInverted() {
  const s = useGameStore.getState();
  s.setCutArrowInverted(!s.settings.cutArrowInverted);
}

function stepSelectedPhase(direction: 1 | -1) {
  const s = useGameStore.getState();
  const next = s.selectedPhaseIndex + direction;
  // selectPhase clamps to [0, unlockedPhaseIndex]; bail at the ends so the
  // stick doesn't feel like it's fighting an invisible wall.
  if (next < 0 || next > s.unlockedPhaseIndex) return;
  s.selectPhase(next);
}

function stepMenuCursor(direction: 1 | -1) {
  const s = useGameStore.getState();
  const order = ["campaign", "survival", "exit"] as const;
  const index = order.indexOf(s.menuCursor);
  s.setMenuCursor(order[(index + direction + order.length) % order.length]);
}

// Thumbstick navigation for the world-space menu. Stick UP/DOWN moves the
// ensō cursor between Campaign/Survival/Exit; Stick LEFT/RIGHT is
// CONTEXTUAL — it steps the campaign phase while the cursor rests on
// Campaign, toggles Single/Dual wield while it rests on Survival (the
// Campaign slot's L/R is already taken by phase stepping; the wield choice
// applies to both modes), or toggles the anonymous-usage-data opt-out while
// it rests on Exit (the DATA row lives right below EXIT in MenuScreen3D for the same
// reason — it's the one item on this screen with nowhere better to sit,
// since the Quest launch path never touches the DOM Menu.tsx equivalent).
// A single "armed" latch per component debounces a held deflection into
// one step, re-arming only once both sticks return near center.
// CUT (whether the target notch points with or against the required cut,
// see DIR_ARROW_ROT in Block.tsx) isn't tied to any menuCursor slot, so it
// isn't part of this stick map — it's grip-driven, in MenuControls below.
function MenuNavControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;
  const armed = useRef(true);

  useFrame(() => {
    if (useGameStore.getState().phase !== "menu") {
      armed.current = true;
      return;
    }

    const readStick = (c: XRControllerState | undefined) =>
      c?.gamepad?.["xr-standard-thumbstick"];
    const ls = readStick(left);
    const rs = readStick(right);
    const x =
      Math.abs(rs?.xAxis ?? 0) > Math.abs(ls?.xAxis ?? 0)
        ? (rs?.xAxis ?? 0)
        : (ls?.xAxis ?? 0);
    const y =
      Math.abs(rs?.yAxis ?? 0) > Math.abs(ls?.yAxis ?? 0)
        ? (rs?.yAxis ?? 0)
        : (ls?.yAxis ?? 0);

    const DEFLECT = 0.7;
    const CENTER = 0.25;

    if (!armed.current) {
      if (Math.abs(x) < CENTER && Math.abs(y) < CENTER) armed.current = true;
      return;
    }

    if (useGameStore.getState().menuExitConfirm) {
      if (Math.abs(y) >= DEFLECT) {
        const state = useGameStore.getState();
        state.setMenuExitCursor(
          state.menuExitCursor === "cancel" ? "exit" : "cancel",
        );
        armed.current = false;
      }
      return;
    }

    // Horizontal wins ties — phase/wield-stepping is the more common intent.
    if (Math.abs(x) >= DEFLECT && Math.abs(x) >= Math.abs(y)) {
      const cursor = useGameStore.getState().menuCursor;
      if (cursor === "campaign") {
        stepSelectedPhase(x > 0 ? 1 : -1);
      } else if (cursor === "survival") {
        cycleWield();
      } else if (cursor === "exit") {
        toggleTelemetryOptOut();
      }
      armed.current = false;
    } else if (Math.abs(y) >= DEFLECT) {
      stepMenuCursor(y > 0 ? 1 : -1);
      armed.current = false;
    }
  });

  return null;
}

function SurvivalIntroControls() {
  const right = useXRInputSourceState(
    "controller",
    "right",
  ) as XRControllerState;
  const left = useXRInputSourceState("controller", "left") as XRControllerState;

  useXRControllerButtonEvent(right, "a-button", (state) => {
    if (
      state === "pressed" &&
      useGameStore.getState().phase === "survival-intro"
    )
      startSurvivalRun();
  });
  useXRControllerButtonEvent(left, "x-button", (state) => {
    if (
      state === "pressed" &&
      useGameStore.getState().phase === "survival-intro"
    )
      startSurvivalRun();
  });
  useXRControllerButtonEvent(right, "b-button", (state) => {
    if (state === "pressed") exitSurvivalIntro();
  });
  useXRControllerButtonEvent(left, "y-button", (state) => {
    if (state === "pressed") exitSurvivalIntro();
  });

  return null;
}

const MENU_WIDTH = 2.25;
const MENU_HEIGHT = 1.58;
// Rough enclosing box for the action-tier text ("CAMPAIGN"/"SURVIVAL",
// both 8 characters at the same tier/tracking) — tuned visually, not
// measured, since troika doesn't expose synchronous text width. Revisit
// on-device per the project's generous-then-tighten calibration approach.
const ENSO_WIDTH = 0.62;
const ENSO_HEIGHT = 0.11;

function MenuScreen3D() {
  const selectedPhaseIndex = useGameStore((s) => s.selectedPhaseIndex);
  const survivalBest = useGameStore((s) => s.survivalBest);
  const settings = useGameStore((s) => s.settings);
  const menuCursor = useGameStore((s) => s.menuCursor);
  const menuExitConfirm = useGameStore((s) => s.menuExitConfirm);
  const menuExitCursor = useGameStore((s) => s.menuExitCursor);
  const telemetryOptOut = useGameStore((s) => s.telemetryOptOut);
  const selectedPhase = CAMPAIGN_PHASES[selectedPhaseIndex];
  const cursorOnExit = menuCursor === "exit";

  const hint =
    menuCursor === "campaign"
      ? "Stick L/R · Phase    Stick U/D · Select    Trigger · Confirm"
      : menuCursor === "survival"
        ? "Stick L/R · Wield    Stick U/D · Select    Trigger · Confirm"
        : "Stick L/R · Data    Stick U/D · Select    Trigger · Confirm";

  if (menuExitConfirm) {
    return (
      <group position={[0, 1.6, -1.9]}>
        <PanelErrorBoundary label="MenuExitConfirmation3D">
          <Suspense fallback={null}>
            <ShojiBacking width={1.85} height={1.15} occluded={false}>
              <PanelText tier="title" position={[0, 0.39, 0]} fontSize={0.085}>
                LEAVE SAMURAI QUEST?
              </PanelText>
              <PanelText tier="secondary" position={[0, 0.25, 0]}>
                Progress and records are saved
              </PanelText>

              <PanelText
                tier="action"
                position={[0, 0.04, 0]}
                color={menuExitCursor === "cancel" ? INK.title : INK.dim}
              >
                CANCEL
              </PanelText>
              <GlyphChip label="B / Y" position={[0, -0.035, 0]} />

              <PanelText
                tier="action"
                position={[0, -0.22, 0]}
                color={menuExitCursor === "exit" ? INK.title : INK.dim}
              >
                EXIT
              </PanelText>
              <GlyphChip label="A / X" position={[0, -0.295, 0]} />

              <Enso
                width={ENSO_WIDTH}
                height={ENSO_HEIGHT}
                position={[0, menuExitCursor === "cancel" ? 0.04 : -0.22, 0]}
              />

              <PanelText tier="footerHint" position={[0, -0.45, 0]}>
                Stick U/D · Select Trigger · Confirm
              </PanelText>
            </ShojiBacking>
          </Suspense>
        </PanelErrorBoundary>
      </group>
    );
  }

  return (
    <group position={[0, 1.6, -1.9]}>
      <PanelErrorBoundary label="MenuScreen3D">
        <Suspense fallback={null}>
          <ShojiBacking width={MENU_WIDTH} height={MENU_HEIGHT}>
            <PanelText tier="title" position={[0, 0.67, 0]} fontSize={0.115}>
              SAMURAI QUEST
            </PanelText>
            <PanelText tier="titleAccent" position={[0, 0.585, 0]}>
              侍クエスト
            </PanelText>

            {/* Primary tier — Campaign. */}
            <PanelText tier="action" position={[0, 0.43, 0]}>
              CAMPAIGN
            </PanelText>
            <GlyphChip label="B / Y" position={[0, 0.355, 0]} />
            <PanelText
              tier="secondary"
              position={[0, 0.26, 0]}
              color={INK.accent}
            >
              {`< ${selectedPhase.numeral} · ${selectedPhase.name} >`}
            </PanelText>

            {/* Primary tier — Survival. */}
            <PanelText tier="action" position={[0, 0.1, 0]}>
              SURVIVAL
            </PanelText>
            <GlyphChip label="A / X" position={[0, 0.025, 0]} />
            <PanelText tier="secondary" position={[0, -0.06, 0]}>
              {`Best ${survivalBest.toLocaleString("en-US")}`}
            </PanelText>

            {/* Selection ring — marks the item Trigger will confirm. */}
            <Enso
              width={ENSO_WIDTH}
              height={ENSO_HEIGHT}
              position={[
                0,
                menuCursor === "campaign"
                  ? 0.43
                  : menuCursor === "survival"
                    ? 0.1
                    : -0.25,
                0,
              ]}
            />

            {/* Exit is deliberately separated from the two play modes. */}
            <PanelText tier="action" position={[0, -0.25, 0]}>
              EXIT
            </PanelText>
            <GlyphChip label="TRIGGER" position={[0, -0.325, 0]} />

            {/* Secondary tier — wield selector. */}
            <PanelText
              tier="secondary"
              position={[-0.5, -0.44, 0]}
              anchorX="left"
            >
              WIELD
            </PanelText>
            <PanelText
              tier="chip"
              position={[-0.14, -0.44, 0]}
              anchorX="right"
              color={settings.wield === "single" ? INK.title : INK.dim}
            >
              single
            </PanelText>
            <PanelText
              tier="chip"
              position={[0.14, -0.44, 0]}
              anchorX="left"
              color={settings.wield === "dual" ? INK.title : INK.dim}
            >
              dual
            </PanelText>
            <PanelRule
              length={0.16}
              position={[settings.wield === "single" ? -0.19 : 0.19, -0.466, 0]}
            />

            {/* Secondary tier — cut arrow reading. Not tied to a menuCursor
                slot (grip-driven, see MenuControls), so its color never dims
                contextually the way DATA's does. */}
            <PanelText
              tier="secondary"
              position={[-0.5, -0.515, 0]}
              anchorX="left"
            >
              CUT
            </PanelText>
            <PanelText
              tier="chip"
              position={[-0.14, -0.515, 0]}
              anchorX="right"
              color={!settings.cutArrowInverted ? INK.title : INK.dim}
            >
              follow
            </PanelText>
            <PanelText
              tier="chip"
              position={[0.14, -0.515, 0]}
              anchorX="left"
              color={settings.cutArrowInverted ? INK.title : INK.dim}
            >
              against
            </PanelText>
            <PanelRule
              length={0.16}
              position={[settings.cutArrowInverted ? 0.19 : -0.19, -0.541, 0]}
            />
            <GlyphChip
              label="GRIP"
              position={[0.34, -0.515, 0]}
              anchorX="left"
            />

            {/* Tertiary tier — anonymous usage data opt-out. Contextual on
                the Exit cursor (see MenuNavControls) since Exit is the one
                item with no wield-style secondary control of its own. */}
            <PanelText
              tier="secondary"
              position={[-0.5, -0.59, 0]}
              anchorX="left"
              color={cursorOnExit ? INK.accent : INK.dim}
            >
              DATA
            </PanelText>
            <PanelText
              tier="chip"
              position={[-0.14, -0.59, 0]}
              anchorX="right"
              color={cursorOnExit && !telemetryOptOut ? INK.title : INK.dim}
            >
              share
            </PanelText>
            <PanelText
              tier="chip"
              position={[0.14, -0.59, 0]}
              anchorX="left"
              color={cursorOnExit && telemetryOptOut ? INK.title : INK.dim}
            >
              private
            </PanelText>
            <PanelRule
              length={0.16}
              position={[telemetryOptOut ? 0.19 : -0.19, -0.616, 0]}
            />

            <PanelText tier="footerHint" position={[0, -0.665, 0]}>
              {hint}
            </PanelText>
            <PanelText
              tier="footerHint"
              position={[0, -0.74, 0]}
              fillOpacity={0.34}
            >
              {`${settings.height} · ${settings.wield === "single" ? settings.singleHand : "both hands"}`}
            </PanelText>
          </ShojiBacking>
        </Suspense>
      </PanelErrorBoundary>
    </group>
  );
}

// occluded={false}: the only screen in the redesign that must stay
// unconditionally on top — frozen mid-air gameplay blocks (paused, not
// removed) could sit in front of this panel's z, and the player needs
// "B/Y to resume" readable regardless. See theme3d.ts's occlusion
// convention and ShojiBacking's doc.
function PauseScreen() {
  return (
    <group position={[0, 1.6, -1.8]}>
      <PanelErrorBoundary label="PauseScreen">
        <Suspense fallback={null}>
          <ShojiBacking width={1.7} height={1.2} occluded={false}>
            <PanelText
              tier="countdownKanji"
              position={[0, 0.3, 0]}
              fontSize={0.34}
              outlineWidth={0.012}
              outlineColor="#330000"
            >
              休
            </PanelText>
            <PanelText
              tier="action"
              position={[0, -0.03, 0]}
              fontSize={0.1}
              color={INK.paper}
              letterSpacing={0.12}
            >
              PAUSED
            </PanelText>
            <PanelText
              tier="actionSub"
              position={[0, -0.24, 0]}
              fontSize={0.06}
              color={INK.accent}
              letterSpacing={0.04}
            >
              B / Y to resume
            </PanelText>
            <PanelText
              tier="actionSub"
              position={[0, -0.36, 0]}
              fontSize={0.06}
              color={INK.accent}
              letterSpacing={0.04}
            >
              A / X to restart
            </PanelText>
            <PanelText
              tier="actionSub"
              position={[0, -0.48, 0]}
              fontSize={0.06}
              color={INK.accent}
              letterSpacing={0.04}
            >
              Active-hand thumbstick to exit
            </PanelText>
          </ShojiBacking>
        </Suspense>
      </PanelErrorBoundary>
    </group>
  );
}

// ─── Pause overlay (DOM, desktop mouse — mirrors ResultOverlay's pattern.
// Doesn't render inside an active immersive session, same as ResultOverlay;
// PauseControls' controller bindings above are the VR-reachable equivalent) ──
function PauseOverlay() {
  const phase = useGameStore((s) => s.phase);
  if (phase !== "paused") return null;

  return (
    <div style={overlayActions}>
      <button onClick={exitToMenu} style={secondaryAction}>
        Main Menu
      </button>
      <button onClick={restartRun} style={secondaryAction}>
        Retry
      </button>
      <button onClick={togglePause} style={primaryAction}>
        Resume
      </button>
    </div>
  );
}

const overlayActions: React.CSSProperties = {
  position: "fixed",
  bottom: 40,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 16,
};

const primaryAction: React.CSSProperties = {
  ...primaryButton,
  ...typography.ui,
  padding: "14px 34px",
  fontSize: 16,
};

const secondaryAction: React.CSSProperties = {
  ...secondaryButton,
  ...typography.ui,
  padding: "14px 30px",
  fontSize: 16,
};

// ─── Kanji countdown with punch animation ────────────────────────────────────
const COUNTDOWN_STEPS = [
  { label: "三", sub: "THREE" },
  { label: "二", sub: "TWO" },
  { label: "一", sub: "ONE" },
  { label: "始め", sub: "BEGIN · HAJIME" },
];

function Countdown({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [scale, setScale] = useState(1.6);
  const doneRef = useRef(false);

  useEffect(() => {
    // Punch-in scale animation per step
    setScale(1.6);
    const scaleId = setTimeout(() => setScale(1.0), 60);

    if (step >= COUNTDOWN_STEPS.length - 1) {
      // Last step ("始め") — trigger onDone after brief display
      const id = setTimeout(() => {
        if (!doneRef.current) {
          doneRef.current = true;
          onDone();
        }
      }, 700);
      return () => {
        clearTimeout(id);
        clearTimeout(scaleId);
      };
    }

    const id = setTimeout(() => setStep((s) => s + 1), 900);
    return () => {
      clearTimeout(id);
      clearTimeout(scaleId);
    };
  }, [step]);

  const current = COUNTDOWN_STEPS[step];

  return (
    <group position={[0, 1.7, -1.8]}>
      <PanelErrorBoundary label="Countdown">
        <Suspense fallback={null}>
          <PanelText
            tier="countdownKanji"
            fontSize={0.55 * scale}
            outlineWidth={0.012}
            outlineColor="#330000"
          >
            {current.label}
          </PanelText>
          {current.sub && (
            <PanelText
              tier="actionSub"
              position={[0, -0.5, 0]}
              fontSize={0.12}
              color={INK.accent}
            >
              {current.sub}
            </PanelText>
          )}
        </Suspense>
      </PanelErrorBoundary>
    </group>
  );
}

// ─── Vignette transition overlay ─────────────────────────────────────────────
function VignetteOverlay() {
  const phase = useGameStore((s) => s.phase);
  const [opacity, setOpacity] = useState(0);
  const prevPhase = useRef(phase);

  useEffect(() => {
    if (prevPhase.current === phase) return;
    prevPhase.current = phase;
    setOpacity(1);
    const id = setTimeout(() => setOpacity(0), 350);
    return () => clearTimeout(id);
  }, [phase]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "black",
        opacity,
        transition: "opacity 0.35s ease",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />
  );
}

// ─── Bloom (post-processing) ──────────────────────────────────────────────────
// Per .extras/design/handoff-design.md §4: the highest-impact visual step, but
// also the highest-risk one — full-screen EffectComposer post renders to its
// own targets, and WebXR immersive sessions render per-eye to a special
// framebuffer; these don't always compose cleanly. The doc calls for a 1-hour
// The on-device spike showed the current composer path black-screens on Quest,
// so bloom stays available as an explicit desktop A/B toggle while sword
// fake-glow carries the default on-device look.
function BloomEffect() {
  return (
    // multisampling=0: the XR framebuffer already handles AA — a second pass
    // here is pure fill-rate cost for no visible gain. resolutionScale halves
    // the bloom pass's render target, per the doc's "half-res bloom is
    // usually indistinguishable and much cheaper" guidance (§4b).
    <EffectComposer multisampling={0} resolutionScale={0.5}>
      <Bloom
        mipmapBlur
        luminanceThreshold={0.25}
        luminanceSmoothing={0.08}
        intensity={2.6}
        radius={0.85}
      />
    </EffectComposer>
  );
}

// ─── PWA auto-enter VR ────────────────────────────────────────────────────────
// Meta Quest launches the installed TWA straight into an immersive-only
// activity: the page itself must request the XR session right after load
// (the icon tap counts as the user activation), or the OS abandons the launch
// in the system loading environment and kicks the user back to Home after a
// few seconds. This must live INSIDE <XR> — calling xrStore.enterVR() from
// the app root raced the Canvas mount and rejected with "not connected to
// three.js" before the browser ever saw a session request (confirmed via
// remote DevTools against the installed build on-device).
let pwaAutoEnterAttempted = false;

function PwaAutoEnterVR({ onSettled }: { onSettled: () => void }) {
  useEffect(() => {
    if (pwaAutoEnterAttempted || !isPwaLaunchContext()) return;
    pwaAutoEnterAttempted = true;
    useGameStore.getState().setBloomEnabled(false);
    const attempt = (retriesLeft: number) => {
      xrStore
        .enterVR()
        .then(() => onSettled())
        .catch((error) => {
          if (retriesLeft > 0) {
            setTimeout(() => attempt(retriesLeft - 1), 300);
            return;
          }
          console.warn("Samurai Quest PWA immersive launch failed.", error);
          onSettled();
        });
    };
    attempt(3);
  }, [onSettled]);
  return null;
}

// ─── 3D Scene ─────────────────────────────────────────────────────────────────
function Scene({
  bloomEnabled,
  onPwaEnterVrSettled,
  onExitApplication,
}: {
  bloomEnabled: boolean;
  onPwaEnterVrSettled: () => void;
  onExitApplication: () => void;
}) {
  const phase = useGameStore((s) => s.phase);
  const mode = useGameStore((s) => s.mode);
  const heightMode = useGameStore((s) => s.settings.height);
  const setPhase = useGameStore((s) => s.setPhase);
  const completeCampaignRun = useGameStore((s) => s.completeCampaignRun);

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 1.6, 0]} fov={80} />
      <GsapTicker />
      <Dojo />
      <HitFlashPool />
      {bloomEnabled && <BloomEffect />}

      {phase === "menu" && <MenuScreen3D />}
      <SurvivalIntro3D />

      {phase === "countdown" && (
        <Countdown onDone={() => setPhase("playing")} />
      )}

      <TeachLessonPanel3D />
      <TutorialDemo3D />

      {(phase === "playing" || phase === "paused") && <HUD />}

      {phase === "paused" && <PauseScreen />}

      {mode === "campaign" ? (
        <GameManager onWin={completeCampaignRun} />
      ) : (
        <SurvivalManager />
      )}

      <ResultScreen3D />

      {phase === "kata" && <KataDemo />}
      {phase === "story" && <StoryBeat />}

      <XR store={xrStore}>
        <XROrigin
          position={[0, heightMode === "seated" ? SEATED_HEIGHT_OFFSET : 0, 0]}
        />
        <PauseControls />
        <MenuControls onExitApplication={onExitApplication} />
        <MenuNavControls />
        <SurvivalIntroControls />
        <ResultControls />
        <StorySkipControls />
        <TutorialControls />
        <TutorialNavControls />
        <BloomToggleControls />
        <SwordGlowToggleControls />
        <PwaAutoEnterVR onSettled={onPwaEnterVrSettled} />
      </XR>
    </>
  );
}

// ─── App root ─────────────────────────────────────────────────────────────────
const MENU_THEME_SRC = "assets/audio/samurai-quest-theme-1.mp3";
// Android `intent://` URI, not a bare `samuraiquest://exit`. Inside the Quest
// TWA the Meta Browser treats a bare custom-scheme navigation as an in-page
// load: it aborts the WebXR session and hangs on a black loading interstitial
// without ever dispatching an Android intent (confirmed via logcat — ExitActivity
// never fires). The `intent://…#Intent;…;end` form is the mechanism Chromium
// documents for launching an Android Intent from web content; the explicit
// `package=` skips the disambiguation prompt and routes straight to ExitActivity,
// which finishAndRemoveTask()s the whole TWA task.
const NATIVE_EXIT_URL =
  "intent://exit#Intent;scheme=samuraiquest;package=com.samuraiquestpreview.vr;end";

export function App() {
  const phase = useGameStore((s) => s.phase);
  // Bloom defaults off after the Quest black-screen spike. Shift+B (desktop) /
  // right thumbstick click (VR) remains available for A/B testing.
  const bloomEnabled = useGameStore((s) => s.bloomEnabled);
  const [menuThemePaused, setMenuThemePaused] = useState(false);
  const [exitComplete, setExitComplete] = useState(false);
  const exitInProgressRef = useRef(false);
  const phaseTrackActive =
    phase === "survival-intro" ||
    phase === "countdown" ||
    phase === "playing" ||
    phase === "paused";
  const menuThemeAllowed = !phaseTrackActive;
  const shouldPlayMenuTheme = menuThemeAllowed && !menuThemePaused;
  const shouldPlayMenuThemeRef = useRef(shouldPlayMenuTheme);
  const {
    play: playMenuTheme,
    pause: pauseMenuTheme,
    resume: resumeMenuTheme,
    isPlaying: menuThemePlaying,
  } = useTrack(menuThemeAllowed ? MENU_THEME_SRC : null, {
    loop: true,
    volume: 0.36,
    fallbackOnPlayError: false,
  });
  // Gates the menu theme's own autoplay attempt until the PWA auto-enterVR
  // request (<PwaAutoEnterVR/>, inside the <XR> tree) has settled. On Quest's
  // TWA launch path, entering an
  // immersive session forces the browser to renegotiate audio focus; an
  // <audio>.play()/mute() call landing in that same window has been observed
  // to crash the Oculus Browser process itself (uncaught exception in
  // MediaSessionImpl.mediaSessionStateChanged, confirmed via logcat — the
  // same page loads fine as a plain browser tab, which never calls
  // enterVR()). Starting `true` when not a PWA launch keeps desktop/browser
  // behavior unchanged, since there's nothing to wait for.
  const [pwaEnterVrSettled, setPwaEnterVrSettled] = useState(
    () => !isPwaLaunchContext(),
  );
  const handlePwaEnterVrSettled = useCallback(
    () => setPwaEnterVrSettled(true),
    [],
  );

  const handleEnterVR = () => {
    useGameStore.getState().setBloomEnabled(false);
    void xrStore.enterVR().catch(() => undefined);
  };

  const handleStartSurvival = () => {
    const status = useGameStore.getState().survivalAudioStatus;
    startSurvivalRun();
    if (status !== "ready") return;
    handleEnterVR();
  };

  const handleToggleMenuTheme = () => {
    const nextPaused = !menuThemePaused;
    setMenuThemePaused(nextPaused);
    if (nextPaused) {
      pauseMenuTheme();
      return;
    }
    if (!menuThemeAllowed) return;
    if (menuThemePlaying) resumeMenuTheme();
    else playMenuTheme();
  };

  const handleExitApplication = useCallback(async () => {
    if (useGameStore.getState().phase !== "menu" || exitInProgressRef.current)
      return;

    exitInProgressRef.current = true;
    setMenuThemePaused(true);
    shouldPlayMenuThemeRef.current = false;
    pauseMenuTheme();

    // Best-effort: give the last queued events a brief window before the
    // teardown below kills the process (native exit) or ends the XR session.
    // Never blocks the exit UX — a timeout races the flush, and whatever
    // doesn't make it out stays in the persisted queue for next launch.
    await Promise.race([
      flush(),
      new Promise((resolve) => setTimeout(resolve, 800)),
    ]);

    // The installed Quest build is a TWA: window.close() cannot finish the
    // Android task that owns the Meta Browser activity. Hand control directly
    // to ExitActivity instead of ending WebXR first; the native activity calls
    // finishAndRemoveTask(), which tears down the TWA and its XR session as one
    // terminal operation and avoids exposing the headset loading environment.
    if (isNativeQuestWrapper()) {
      window.location.assign(NATIVE_EXIT_URL);
      return;
    }

    const session = xrStore.getState().session;
    if (session) {
      try {
        // XRSession.end() is the standards-defined escape hatch from an
        // immersive WebXR application. Wait for the runtime to finish its
        // platform shutdown before replacing the UI behind the session.
        await session.end();
      } catch (error) {
        console.warn("Samurai Quest could not end the XR session.", error);
        useGameStore.getState().setMenuExitConfirm(false);
        setMenuThemePaused(false);
        shouldPlayMenuThemeRef.current = menuThemeAllowed;
        exitInProgressRef.current = false;
        if (menuThemeAllowed) resumeMenuTheme();
        return;
      }
    }

    setExitComplete(true);
    // Browsers only allow scripts to close windows in limited launch
    // contexts. Installed PWAs/TWAs may honor this; regular tabs fall back to
    // the explicit closed-state screen rendered below.
    window.close();
  }, [menuThemeAllowed, pauseMenuTheme, resumeMenuTheme]);

  useEffect(() => {
    shouldPlayMenuThemeRef.current = shouldPlayMenuTheme;
  }, [shouldPlayMenuTheme]);

  // Pause everything when the player actually leaves the game (headset
  // removed, Universal Menu, tab backgrounded) — VRC.Quest.Functional.2, and
  // it also keeps a backgrounded tab coherent: without this the audio clock
  // kept running invisibly, phases "finished" on their own, and relaunching
  // the installed app landed on a stale pause/result screen.
  //
  // Two complementary signals, never both at once:
  // - While an immersive session presents, the Quest marks the 2D document
  //   "hidden" the whole time — that's presentation, not backgrounding, so
  //   document visibility must be ignored then. The XRSession's own
  //   visibilitychange (fires for Universal Menu / headset removal) owns
  //   that case instead.
  // - With no session, plain document visibility covers the 2D tab.
  useEffect(() => {
    const pauseForInterruption = () => {
      if (useGameStore.getState().phase === "playing") {
        useGameStore.getState().setPhase("paused");
      }
      pauseMenuTheme();
    };

    const handleDocumentVisibility = () => {
      if (xrStore.getState().session) return;
      if (document.visibilityState === "hidden") {
        pauseForInterruption();
        return;
      }
      if (shouldPlayMenuThemeRef.current) resumeMenuTheme();
    };

    let detachSession: (() => void) | undefined;
    let sessionStartedAt: number | null = null;
    const watchSession = (session: XRSession | undefined) => {
      detachSession?.();
      detachSession = undefined;
      if (!session) {
        if (sessionStartedAt != null) {
          const durationS = (performance.now() - sessionStartedAt) / 1000;
          sessionStartedAt = null;
          track("xr_session_end", {
            duration_s: Math.round(durationS * 10) / 10,
          });
        }
        return;
      }
      sessionStartedAt = performance.now();
      track("xr_session_start", { mode: useGameStore.getState().mode });
      const handleSessionVisibility = () => {
        if (session.visibilityState !== "visible") {
          pauseForInterruption();
          return;
        }
        if (shouldPlayMenuThemeRef.current) resumeMenuTheme();
      };
      session.addEventListener("visibilitychange", handleSessionVisibility);
      detachSession = () =>
        session.removeEventListener(
          "visibilitychange",
          handleSessionVisibility,
        );
    };

    watchSession(xrStore.getState().session);
    const unsubscribe = xrStore.subscribe((state, prev) => {
      if (state.session !== prev.session) watchSession(state.session);
    });
    document.addEventListener("visibilitychange", handleDocumentVisibility);
    return () => {
      unsubscribe();
      detachSession?.();
      document.removeEventListener(
        "visibilitychange",
        handleDocumentVisibility,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!menuThemeAllowed) {
      pauseMenuTheme();
      return;
    }
    if (menuThemePaused) {
      pauseMenuTheme();
      return;
    }
    if (menuThemePlaying) {
      resumeMenuTheme();
      return;
    }
    // Don't fire the programmatic (gesture-less) autoplay attempt until the
    // PWA auto-enterVR request has settled — see pwaEnterVrSettled above.
    if (!pwaEnterVrSettled) return;
    playMenuTheme();
  }, [menuThemeAllowed, menuThemePaused, pwaEnterVrSettled]);

  useEffect(() => {
    if (!shouldPlayMenuTheme || menuThemePlaying) return;

    const startAfterGesture = () => {
      if (shouldPlayMenuThemeRef.current) playMenuTheme();
    };

    window.addEventListener("pointerdown", startAfterGesture, {
      once: true,
      capture: true,
    });
    window.addEventListener("keydown", startAfterGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", startAfterGesture, true);
      window.removeEventListener("keydown", startAfterGesture);
    };
  }, [shouldPlayMenuTheme, menuThemePlaying]);

  // Desktop fallback for pause (controller B/Y handles it in VR)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "p" || e.key === "P") togglePause();
      if (e.shiftKey && (e.key === "b" || e.key === "B"))
        toggleBloomForDesktopOnly();
      if (e.shiftKey && (e.key === "g" || e.key === "G")) {
        useGameStore.getState().toggleSwordGlow();
        console.info(
          `Sword glow ${useGameStore.getState().swordGlowEnabled ? "on" : "off"}`,
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lessons (`teach`) are DOM-only briefing screens — they never render inside
  // an immersive session. Any transition that lands on `teach` while VR is
  // active (advancing to the next phase's lesson from the result screen, story
  // → next phase, etc.) must end the session, or the player is stranded in VR
  // staring at an empty scene while the lesson card sits invisible in the DOM.
  // NOTE: this used to end the session on phase "teach" (from before the
  // lesson had a world-space panel). TeachPanel3D now renders the full
  // teach/consolidate flow inside VR, and in the installed PWA ending the
  // session strands the player (there is no 2D page behind it) — it was
  // exactly why the campaign never started from the in-VR menu.
  useEffect(() => {
    if (phase === "teach" && !isPwaLaunchContext()) {
      void xrStore.getState().session?.end();
    }
  }, [phase]);

  if (exitComplete) {
    return (
      <main style={exitCompleteScreen}>
        <div style={exitCompleteKanji}>終</div>
        <h1 style={exitCompleteTitle}>SAMURAI QUEST CLOSED</h1>
        <p style={exitCompleteCopy}>You can now close this window safely.</p>
      </main>
    );
  }

  return (
    <>
      <Canvas style={{ position: "fixed", width: "100vw", height: "100vh" }}>
        <Scene
          bloomEnabled={bloomEnabled}
          onPwaEnterVrSettled={handlePwaEnterVrSettled}
          onExitApplication={() => void handleExitApplication()}
        />
      </Canvas>

      {phase === "menu" && (
        <Menu
          onEnterVR={handleEnterVR}
          menuThemePaused={menuThemePaused}
          onToggleMenuTheme={handleToggleMenuTheme}
          onExit={() => void handleExitApplication()}
        />
      )}
      <SurvivalIntroOverlay
        onStart={handleStartSurvival}
        onMenu={exitSurvivalIntro}
      />
      <TutorialBriefingScreen onEnterDemo={handleEnterVR} />

      {phase === "kata" && <SkillTreeScreen />}

      <ResultOverlay />

      <StoryOverlay />

      <TutorialOverlay />

      <PauseOverlay />

      <VignetteOverlay />
    </>
  );
}

const exitCompleteScreen: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: INK.paper,
  background: "#14100e",
  ...typography.ui,
};

const exitCompleteKanji: React.CSSProperties = {
  ...typography.display,
  color: INK.accent,
  fontSize: 96,
  lineHeight: 1,
};

const exitCompleteTitle: React.CSSProperties = {
  margin: "24px 0 0",
  fontSize: 20,
  letterSpacing: "0.14em",
};

const exitCompleteCopy: React.CSSProperties = {
  margin: "14px 0 0",
  color: INK.dim,
  fontSize: 14,
};
